import type { Inventory, ItemDef, Player } from './types';

export function countItem(inventory: Inventory, itemId: string): number {
  let total = 0;
  for (const stack of inventory.slots) {
    if (stack.itemId === itemId) total += stack.quantity;
  }
  return total;
}

export function hasItem(inventory: Inventory, itemId: string, quantity: number = 1): boolean {
  return countItem(inventory, itemId) >= quantity;
}

export function addItem(
  inventory: Inventory,
  item: ItemDef,
  quantity: number = 1,
): { inventory: Inventory; added: number } {
  if (quantity <= 0) return { inventory, added: 0 };

  const slots = inventory.slots.map((s) => ({ ...s }));

  if (item.stackable) {
    const idx = slots.findIndex((s) => s.itemId === item.id);
    if (idx >= 0) {
      slots[idx].quantity += quantity;
      return { inventory: { ...inventory, slots }, added: quantity };
    }
  }

  let remaining = quantity;
  let added = 0;
  while (remaining > 0 && slots.length < inventory.maxSlots) {
    const take = item.stackable ? remaining : 1;
    slots.push({ itemId: item.id, quantity: take });
    remaining -= take;
    added += take;
  }

  return { inventory: { ...inventory, slots }, added };
}

export function removeItem(
  inventory: Inventory,
  itemId: string,
  quantity: number = 1,
): { inventory: Inventory; ok: boolean } {
  if (quantity <= 0) return { inventory, ok: true };
  if (!hasItem(inventory, itemId, quantity)) return { inventory, ok: false };

  const slots = inventory.slots.map((s) => ({ ...s }));
  let remaining = quantity;

  for (let i = slots.length - 1; i >= 0 && remaining > 0; i--) {
    if (slots[i].itemId !== itemId) continue;
    const take = Math.min(slots[i].quantity, remaining);
    slots[i].quantity -= take;
    remaining -= take;
    if (slots[i].quantity <= 0) {
      slots.splice(i, 1);
    }
  }

  return { inventory: { ...inventory, slots }, ok: true };
}

export function withInventory(player: Player, inventory: Inventory, now: number = Date.now()): Player {
  return { ...player, inventory, updatedAt: now };
}
